
import type { PropType } from 'vue'
import type { EpbSelectBackground, EpbSelectOption, EpbSelectOptionType } from './epb-select.types'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import { ArrayUtils } from '../../../utils/array.utils'
import { EpbBaseInput } from '../epb-base-input'
import { EpbInputHelp } from '../epb-input-help'
import EpbSelectDropdown from './epb-select-dropdown.component.vue'

export default defineComponent({
  name: 'EpbSelect',
  components: {
    EpbBaseInput,
    EpbInputHelp,
    EpbSelectDropdown,
  },
  props: {
    value: {
      type: [String, Array] as PropType<string | Array<string>>,
      required: true,
    },
    options: {
      type: Array as PropType<Array<EpbSelectOption>>,
      required: true,
    },
    search: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: undefined,
    },
    icon: {
      type: String,
      default: undefined,
    },
    required: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    info: {
      type: String,
      default: undefined,
    },
    help: {
      type: String,
      default: undefined,
    },
    helpIcon: {
      type: String,
      default: undefined,
    },
    background: {
      type: String as PropType<EpbSelectBackground>,
      default: 'none',
    },
    optionType: {
      type: String as PropType<EpbSelectOptionType>,
      default: 'default',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: undefined,
    },
    showSelectAll: {
      type: Boolean,
      default: undefined,
    },
    selectAllLabel: {
      type: String,
      default: undefined,
    },
    multipleDelimiter: {
      type: String,
      default: ', ',
    },
    hierarchyDelimiter: {
      type: String,
      default: ' > ',
    },
    round: Boolean,
    loading: Boolean,
    loadOptions: {
      type: Function as PropType<() => Promise<void>>,
      default: undefined,
    },
    showDropdownWhileLoading: Boolean,
    dropdownCloseDisabled: Boolean,
    showAllInputValueIfAllOptionsSelected: {
      type: Boolean,
      default: true,
    },
    showChildrenOnClick: {
      type: Boolean,
      default: false,
    },
    shouldSearchChildren: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'change'],
  setup(props) {
    const baseClass = 'epb-select'

    const {
      proxy: { $epb, $emit },
    } = getCurrentInstance()

    const messages = $epb.messages.filters

    const input = ref()
    const dropdown = ref()

    const isOpen = ref(false)
    const isLoadingOptions = ref(false)

    const isLoading = computed(() => props.loading || isLoadingOptions.value)

    const isDisabled = computed(() => props.disabled || isLoading.value)

    const isDropdownVisible = computed(() => !isLoading.value || props.showDropdownWhileLoading)

    const classes = computed<Array<string | Record<string, boolean>>>(() => [
      baseClass,
      `${baseClass}--bg-${props.background}`,
      {
        [`${baseClass}--info`]: !!(props.info || props.error),
        [`${baseClass}--error`]: !!props.error,
        [`${baseClass}--open`]: isOpen.value,
        [`${baseClass}--round`]: props.round,
      },
    ])

    // Removes selected value(s) for non-existing options
    const filteredValue = computed(() => {
      const isValidOptionValue = (optionValue: string | number): boolean => {
        const foundOptions = ArrayUtils.findInHierarchy(optionValue, props.options, 'value')
        return foundOptions.length > 0
      }

      if (Array.isArray(props.value)) {
        return props.value.filter(isValidOptionValue)
      }

      return isValidOptionValue(props.value) ? props.value : ''
    })

    const inputValue = computed<string>(() => {
      const arrayValue = [].concat(filteredValue.value || [])

      const allOptionsSelected = arrayValue.length === props.options.length

      if (
        props.options.length > 1 &&
        allOptionsSelected &&
        props.showAllInputValueIfAllOptionsSelected
      ) {
        return messages.values.all.value
      }

      return arrayValue
        .map(value => {
          const valueOptions = ArrayUtils.findInHierarchy(value, props.options, 'value')
          return valueOptions
            .map(option => option.label ?? option.value)
            .join(props.hierarchyDelimiter)
        })
        .join(props.multipleDelimiter)
    })

    async function loadOptions(): Promise<void> {
      if (props.options.length || typeof props.loadOptions !== 'function') {
        return
      }

      isLoadingOptions.value = true
      await props.loadOptions()
      isLoadingOptions.value = false
    }

    async function onOpenDropdown(): Promise<void> {
      await loadOptions()

      const inputWidth = input.value.getBoundingClientRect().width
      dropdown.value.$el.style.width = `${inputWidth}px`
      isOpen.value = true
    }

    function onCloseDropdown(): void {
      isOpen.value = false
    }

    return {
      baseClass,
      classes,
      dropdown,
      input,
      inputValue,
      isOpen,
      isDisabled,
      isDropdownVisible,
      isLoading,
      filteredValue,
      onOpenDropdown,
      onCloseDropdown,
    }
  },
})
